import { Controller } from "@hotwired/stimulus"

// Add-widgets modal (TMS-1073): the catalogue lives in a sidebar, the selected
// widget previews on the right, and add/remove persist immediately as the
// complete visible list. Reset is a separate DELETE. Customize drag/resize is
// a different controller; this one hides while that edit mode is on.
export default class extends Controller {
  static targets = [
    "openButton", "dialog", "title", "list", "item", "preview",
    "addButton", "removeButton", "lockedNote", "resetButton",
    "error", "announcer", "grid"
  ]
  static values = { saveUrl: String, resetUrl: String }

  open() {
    this.errorTarget.hidden = true
    this.dialogTarget.hidden = false
    this.select(this.defaultItem())
    this.titleTarget.focus()
  }

  close() {
    if (this.dialogTarget.hidden) return
    this.dialogTarget.hidden = true
    if (this.hasOpenButtonTarget) this.openButtonTarget.focus()
  }

  closeOnEscape(event) {
    if (this.dialogTarget.hidden) return
    event.preventDefault()
    this.close()
  }

  selectItem(event) {
    this.select(event.currentTarget)
  }

  listKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
    event.preventDefault()
    const items = this.itemTargets
    const current = items.indexOf(event.currentTarget)
    const delta = event.key === "ArrowDown" ? 1 : -1
    const next = items[current + delta]
    if (!next) return
    this.select(next)
    next.focus()
  }

  async add() {
    const key = this.selectedKey()
    if (!key || this.onHub(key)) return

    const template = this.templateFor(key)
    const source = template?.content.firstElementChild
    if (source) this.gridTarget.appendChild(source.cloneNode(true))
    this.markOnHub(key, true)
    this.announce(`${this.titleOf(key)} added to your hub.`)

    const ok = await this.persist()
    if (!ok) this.revertAdd(key)
  }

  async remove() {
    const key = this.selectedKey()
    if (!key || this.locked(key) || !this.onHub(key)) return

    const card = this.cardInGrid(key)
    this.stashCard(key, card)
    this.markOnHub(key, false)
    this.announce(`${this.titleOf(key)} removed from your hub.`)

    const ok = await this.persist()
    if (!ok) this.revertRemove(key)
  }

  async reset() {
    this.resetButtonTarget.disabled = true
    try {
      const response = await this.request(this.resetUrlValue, { method: "DELETE" })
      if (!response.ok) throw new Error(`Reset failed (${response.status})`)
      window.location.reload()
    } catch (error) {
      console.error("Could not reset dashboard widgets:", error)
      this.showError()
      this.resetButtonTarget.disabled = false
    }
  }

  // Internals

  select(item) {
    if (!item) return
    this.itemTargets.forEach(el => {
      const selected = el === item
      el.classList.toggle("is-selected", selected)
      el.setAttribute("aria-selected", selected ? "true" : "false")
    })
    this.renderPreview(item.dataset.widgetKey)
    this.updateActions(item)
  }

  defaultItem() {
    return this.itemTargets.find(item => item.dataset.onHub !== "true") || this.itemTargets[0]
  }

  renderPreview(key) {
    this.previewTarget.replaceChildren()
    const source = this.cardInGrid(key) || this.templateFor(key)?.content.firstElementChild
    if (!source) return

    const clone = source.cloneNode(true)
    this.scrub(clone)
    this.previewTarget.appendChild(clone)
  }

  updateActions(item) {
    const locked = item.dataset.locked === "true"
    const onHub = item.dataset.onHub === "true"
    this.lockedNoteTarget.hidden = !locked
    this.addButtonTarget.hidden = locked || onHub
    this.removeButtonTarget.hidden = locked || !onHub
  }

  markOnHub(key, onHub) {
    const item = this.itemFor(key)
    if (!item) return
    item.dataset.onHub = onHub ? "true" : "false"
    item.classList.toggle("is-on-hub", onHub)
    this.refreshItemChrome(item, onHub)
    this.updateActions(item)
    this.renderPreview(key)
    this.resetButtonTarget.disabled = false
  }

  refreshItemChrome(item, onHub) {
    item.querySelector(".dashboard-widget-picker-item-status")?.remove()
    item.querySelector(".dashboard-widget-picker-item-add")?.remove()
    if (item.dataset.locked === "true") return

    if (onHub) {
      const status = document.createElement("span")
      status.className = "dashboard-widget-picker-item-status"
      status.textContent = "On your hub"
      item.appendChild(status)
    } else {
      const icon = document.createElement("i")
      icon.className = "ph ph-plus dashboard-widget-picker-item-add"
      icon.setAttribute("aria-hidden", "true")
      item.appendChild(icon)
    }
  }

  revertAdd(key) {
    this.cardInGrid(key)?.remove()
    this.markOnHub(key, false)
    this.showError()
  }

  revertRemove(key) {
    const template = this.templateFor(key)
    const card = template?.content.firstElementChild
    if (card) this.gridTarget.appendChild(card)
    this.markOnHub(key, true)
    this.showError()
  }

  stashCard(key, card) {
    if (!card) return
    let template = this.templateFor(key)
    if (!template) {
      template = document.createElement("template")
      template.dataset.widgetTemplate = key
      this.element.appendChild(template)
    }
    template.content.replaceChildren(card)
  }

  async persist() {
    this.errorTarget.hidden = true
    this.element.dataset.pickerSaved = "pending"
    try {
      const response = await this.request(this.saveUrlValue, {
        method: "PATCH",
        body: JSON.stringify({ order: this.visibleKeys() })
      })
      if (!response.ok) throw new Error(`Save failed (${response.status})`)
      this.element.dataset.pickerSaved = "ok"
      return true
    } catch (error) {
      this.element.dataset.pickerSaved = "error"
      console.error("Could not save dashboard widgets:", error)
      return false
    }
  }

  request(url, { method, body }) {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    }
    // Test disables forgery protection, so the csrf-token meta is absent there.
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken

    return fetch(url, {
      method,
      headers,
      credentials: "same-origin",
      body
    })
  }

  visibleKeys() {
    return Array.from(this.gridTarget.querySelectorAll("[data-widget-key]"))
      .map(card => card.dataset.widgetKey)
  }

  selectedKey() {
    return this.itemTargets.find(item => item.classList.contains("is-selected"))?.dataset.widgetKey
  }

  onHub(key) {
    return this.itemFor(key)?.dataset.onHub === "true"
  }

  locked(key) {
    return this.itemFor(key)?.dataset.locked === "true"
  }

  itemFor(key) {
    return this.itemTargets.find(item => item.dataset.widgetKey === key)
  }

  titleOf(key) {
    return this.itemFor(key)?.querySelector(".dashboard-widget-picker-item-title")?.textContent?.trim() || "Widget"
  }

  cardInGrid(key) {
    return this.gridTarget.querySelector(`[data-widget-key="${key}"]`)
  }

  templateFor(key) {
    return this.element.querySelector(`template[data-widget-template="${key}"]`)
  }

  scrub(node) {
    node.removeAttribute("id")
    node.removeAttribute("draggable")
    node.querySelectorAll("[id]").forEach(el => el.removeAttribute("id"))
    node.querySelectorAll("[data-avatar-upload-target]").forEach(el => {
      el.removeAttribute("data-avatar-upload-target")
      el.removeAttribute("data-action")
    })
  }

  announce(message) {
    this.announcerTarget.textContent = message
  }

  showError() {
    this.errorTarget.hidden = false
  }
}
